// What being swallowed DOES, turn by turn — decided here, applied by the tracker.
//
// The rules come from the swallower's own statblock (state/swallowed.ts) and the
// pairs from the attachment kinds on the link table. Everything in this file is
// pure: given who is swallowed by whom and whose turn it is, it says what should
// happen. The tracker does it.
//
// Conditions carry a SOURCE so only ours are taken away again. A DM who marked a
// PC blinded for their own reasons must not lose that mark because a behir let
// go of somebody else, and a creature that was already restrained keeps its own
// restraint when it is regurgitated.
import type { Condition } from "./types";
import type { SwallowRule } from "./swallowed";
import type { TokenLink } from "./tokenLinks";
import { normName as norm } from "./text";

/** The tag on every condition this module puts on a creature. */
export const SWALLOW_SOURCE = "Swallowed";

/** A token as the link table names it: an id, a name, usually both. */
export interface TokenRefLite { id?: string; name?: string }
/** A tracker row, which may or may not know its Roll20 token. */
export interface TrackerRowLite { tokenId?: string; name: string }

export interface SwallowedPair {
  swallower: TokenRefLite;
  victim: TokenRefLite;
  rule: SwallowRule;
}


/**
 * Does this tracker row refer to the same token as this link entry?
 *
 * By id when both know one, by name otherwise. The name fallback is not a nicety:
 * a PARTY member's row carries a tokenId only once the DM has mapped it under
 * "Roll20 token names", so a swallowed player was matched by id against a row
 * that had none — and the victim of a swallow is usually a player. Matching on a
 * composite key alone (which this did at first) meant the common case silently
 * found nobody.
 */
export function refMatches(row: TrackerRowLite, ref: TokenRefLite): boolean {
  const rowId = row.tokenId && row.tokenId !== "-1" ? row.tokenId : "";
  if (ref.id && rowId && ref.id === rowId) return true;
  return Boolean(ref.name) && norm(row.name) === norm(ref.name);
}

const nameOf = (c: string | Condition): string => (typeof c === "string" ? c : String(c.name || c.condition || ""));
const isOurs = (c: string | Condition): boolean => typeof c !== "string" && (c.source as { spell?: string } | undefined)?.spell === SWALLOW_SOURCE;

/** The conditions a swallowed creature should be carrying, tagged as ours. */
export function swallowConditions(pair: SwallowedPair): Condition[] {
  return pair.rule.conditions.map((name) => ({
    name,
    anchorName: pair.swallower.name || "",
    source: { casterId: pair.swallower.id || pair.swallower.name || "", spell: SWALLOW_SOURCE },
  } as unknown as Condition));
}

/**
 * Bring one creature's condition list in line with what it should have.
 *
 * Adds the ones missing, removes only the ones WE added that are no longer
 * wanted, and never touches anything the DM put there. Returns null for `next`
 * when nothing needs to change, so a caller can skip the write entirely rather
 * than re-rendering the tracker on every tick.
 */
export function reconcileConditions(
  existing: (string | Condition)[] | undefined,
  wanted: Condition[],
): { next: (string | Condition)[] | null; added: string[]; removed: string[] } {
  const have = existing || [];
  const wantNames = new Set(wanted.map((c) => nameOf(c).toLowerCase()));
  const haveNames = new Set(have.map((c) => nameOf(c).toLowerCase()));

  const kept = have.filter((c) => !isOurs(c) || wantNames.has(nameOf(c).toLowerCase()));
  const removed = have.filter((c) => isOurs(c) && !wantNames.has(nameOf(c).toLowerCase())).map(nameOf);
  const missing = wanted.filter((c) => !haveNames.has(nameOf(c).toLowerCase()));

  if (!removed.length && !missing.length) return { next: null, added: [], removed: [] };
  return { next: [...kept, ...missing], added: missing.map(nameOf), removed };
}

/** The attachment kind that means "inside it". */
export const SWALLOWED_KIND = "swallowed";

/**
 * Which links say somebody is swallowed, as a string that changes when they do.
 *
 * The pair list is rebuilt from this, and rebuilding costs a round trip to the
 * connector for each swallower, so it must not be rebuilt on every render.
 */
export const swallowSignature = (links: TokenLink[]): string =>
  links
    .flatMap((l) => l.riders.filter((r) => r.kind === SWALLOWED_KIND).map((r) => `${l.base.id || l.base.name}>${r.id || r.name}`))
    .sort()
    .join("|");

/**
 * The pairs, from the links and a rule lookup.
 *
 * Extracted from the tracker component, which is where it went wrong and where
 * nothing could test it. Two mistakes lived here: it built a single composite key
 * per side (so a party row with no Roll20 token id matched nobody — and the
 * victim of a swallow is usually a player), and with the battlemap wrongly
 * chosen as the link's base it asked for the statblock of a .jpg, got nothing,
 * and dropped the pair without a word.
 *
 * `ruleFor` answers null for a creature whose statblock has not been seen and for
 * one that cannot swallow. Both mean "do nothing": a swallow this cannot price is
 * left to the DM rather than guessed at.
 */
export async function swallowPairsFrom(
  links: TokenLink[],
  ruleFor: (name: string) => Promise<SwallowRule | null>,
): Promise<SwallowedPair[]> {
  const out: SwallowedPair[] = [];
  /* ONE pair per swallower-and-victim, by NAME, however many links say it.
     mergeLink used to match a base by id, so re-linking a replaced token stacked
     up duplicate links for the same pair — and one bite per link meant a
     swallowed player took the recurring damage once per copy. That is fixed at
     the source now, but links already saved still hold the duplicates, and a
     creature being eaten twice by the same monster is not a thing this should
     ever represent. */
  const seen = new Set<string>();
  for (const l of links) {
    const swallowed = l.riders.filter((r) => r.kind === SWALLOWED_KIND);
    if (!swallowed.length) continue;
    const rule = await ruleFor(l.base.name);
    if (!rule) continue;
    for (const r of swallowed) {
      const key = `${norm(l.base.name || l.base.id)}>${norm(r.name || r.id)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ swallower: { id: l.base.id, name: l.base.name }, victim: { id: r.id, name: r.name }, rule });
    }
  }
  return out;
}

/** Every pair whose victim is this row. */
export const swallowedOf = (pairs: SwallowedPair[], row: TrackerRowLite): SwallowedPair[] =>
  pairs.filter((p) => refMatches(row, p.victim));

/**
 * The recurring bite due when a turn begins.
 *
 * The SRD anchors it to the swallower: "at the start of each of the behir's
 * turns". So a swallowed creature takes nothing on its own turn, and a second
 * creature swallowed by the same monster takes its own share on that one turn.
 */
export function bitesOnTurn(pairs: SwallowedPair[], turn: TrackerRowLite | null | undefined): { pair: SwallowedPair; dice: string; dmgType: string }[] {
  if (!turn || !turn.name) return [];
  return pairs
    .filter((p) => refMatches(turn, p.swallower))
    .map((p) => ({ pair: p, dice: p.rule.dice, dmgType: p.rule.dmgType }));
}

/** Everyone currently inside a given swallower, for the capacity note and the log. */
export const insideOf = (pairs: SwallowedPair[], swallower: TrackerRowLite): SwallowedPair[] =>
  pairs.filter((p) => refMatches(swallower, p.swallower));

/**
 * Is this swallower holding more than its statblock says it can?
 *
 * Reported, never enforced: the DM may have a reason, and a tracker that refuses
 * a link mid-fight is worse than one that mentions it.
 */
export function overCapacity(pairs: SwallowedPair[], swallower: TrackerRowLite, rule: SwallowRule): boolean {
  return rule.capacity !== null && insideOf(pairs, swallower).length > rule.capacity;
}

/** The line the DM reads when the threshold is met. */
export const regurgitatePrompt = (pair: SwallowedPair): string =>
  pair.rule.threshold === null || pair.rule.saveDc === null
    ? `${pair.swallower.name || "It"} states no way to be forced to give ${pair.victim.name || "them"} up`
    : `${pair.swallower.name || "It"}: DC ${pair.rule.saveDc} ${String(pair.rule.saveAbility).toUpperCase()} or bring ${pair.victim.name || "them"} back up, prone`;
